// Snapshot-derived response envelope shared by every API surface that
// returns rows sourced from a versioned `ParsedSnapshot` (ADR-0006).
//
// The contract pattern every snapshot-derived endpoint follows:
//
//   - Reads default to the `(fileType, scope)`'s `active_version` named by
//     the `SnapshotPointer`.
//   - Every endpoint accepts an optional `?version=<ref>` query parameter
//     resolving to a specific historical `ParsedSnapshot.id`.
//   - Every response wraps its rows together with the `VersionRef` it
//     actually served, so a UI can never silently show a different version
//     than the one the caller asked for.
//
// Surface PRDs (#34, #40, #41, #43, #44) compose `snapshotResponse(rowsT)`
// for their own response and add `SnapshotQuerySchema` to their query
// fragment.

import { z } from 'zod';

/**
 * The serialisable reference identifying which snapshot served a response.
 *
 *  - `versionId` is the `ParsedSnapshot.id` resolved at read time, or
 *    `null` when the `(fileType, scope)` has no `SnapshotPointer` yet
 *    (i.e. the surface has never been uploaded). A null id is "no data
 *    yet, honestly", not 404 — the response carries an empty `rows` array.
 *  - `fileType` and `scope` echo the request the response answers; the
 *    contract carries them on every response so clients do not have to
 *    reassemble the key from out-of-band state.
 *  - `parsedAt` and `versionN` are the served `ParsedSnapshot`'s own
 *    provenance (SEP-184c). They let a surface tell the reader how old the
 *    data it is showing actually is instead of leaving staleness to be
 *    noticed. Both are `null` exactly when `versionId` is `null`.
 *
 * Every field is populated centrally by the server's `readSnapshot` helper,
 * which is the sole producer of this shape.
 */
export const VersionRefSchema = z.object({
    fileType: z.string(),
    scope: z.string(),
    versionId: z.string().nullable(),
    /**
     * ISO-8601 timestamp the served snapshot was parsed at — i.e. when this
     * data entered the firm. `null` when no snapshot resolved.
     */
    parsedAt: z.string().nullable(),
    /**
     * Monotonic version number within `(fileType, scope)`. `null` when no
     * snapshot resolved.
     */
    versionN: z.number().int().positive().nullable(),
});
export type VersionRef = z.infer<typeof VersionRefSchema>;

/**
 * Wrap any snapshot-derived response shape with the `versionRef` it served.
 *
 * Usage:
 *
 *   const overdrawnCashResponse = snapshotResponse(z.array(overdrawnCashRow))
 *
 * The resulting schema is `{ rows: <T>, versionRef: VersionRef }`. Keeping
 * `rows` generic over `T` lets each surface choose its own row shape (an
 * array, a single object, a grouped structure) without re-implementing the
 * version-ref plumbing.
 */
export const snapshotResponse = <T extends z.ZodTypeAny>(rows: T) =>
    z.object({
        rows,
        versionRef: VersionRefSchema,
    });

/**
 * Query-string fragment for `?version=<ref>` — added to every snapshot list
 * endpoint's `query` schema. When omitted, the endpoint resolves the active
 * version; when supplied, the endpoint serves that exact version (and 404s
 * if the ref does not match the requested `(fileType, scope)` or does not
 * exist at all).
 */
export const SnapshotQuerySchema = z.object({
    version: z.string().optional(),
});
export type SnapshotQuery = z.infer<typeof SnapshotQuerySchema>;
